import React, { useEffect, useState } from "react";
import {
  Typography,
  Box,
  Card,
  Grid,
  List,
  ListItem,
  IconButton,
  Tooltip,
  ListItemAvatar,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Select,
  MenuItem,
  DialogActions,
  Menu,
  Collapse,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import StorageIcon from "@mui/icons-material/Storage";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CTLNotification from "application/components/CTLNotification";
import RestoreIcon from "@mui/icons-material/Restore";
import CloudIcon from "@mui/icons-material/Cloud";
import SyncIcon from "@mui/icons-material/Sync";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import RefreshIcon from "@mui/icons-material/Refresh";
import loggerFactory from "globals/logger/logger-factory";
import { application } from "globals/endpoints";
import CTLDialogContent from "application/components/styles/CTLDialogContent";

const ServerBackupRestore = ({ pageName }) => {
  const [backupList, setBackupList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [serverList, setServerList] = useState([]);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [selectedServer, setSelectedServer] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const menuOpen = Boolean(anchorEl);
  const [expandedVersionIndex, setExpandedVersionIndex] = useState(null);
  const [expandedIPIndex, setExpandedIPIndex] = useState({});
  const [expandedBackupIndex, setExpandedBackupIndex] = useState(null);
  const [expandedBackupIPIndex, setExpandedBackupIPIndex] = useState({});
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });

  const fetchServerList = async () => {
    try {
      const listResponse = await application.post(
        "nodeConfiguration/serverList"
      );
      const server = listResponse?.data?.data || [];
      setServerList(server);
      fetchStatusList();
      fetchBackupList();
    } catch (err) {
      loggerFactory.debug("Failed to get serverList list : ", err);
    }
  };

  const fetchStatusList = async () => {
    try {
      const listResponse = await application.post(`sync/getList`);
      setStatusList(listResponse?.data?.data || []);
    } catch (err) {
      loggerFactory.debug("Failed to get sync status list : ", err);
    }
  };

  const fetchBackupList = async () => {
    try {
      const listResponse = await application.post(`sync/backupList`);
      setBackupList(listResponse.data.data || []);
    } catch (err) {
      loggerFactory.debug("Failed to get backupList list : ", err);
    }
  };

  useEffect(() => {
    fetchStatusList();
    fetchBackupList();
    fetchServerList();
  }, []);

  const handleSyncCheck = async () => {
    try {
      const response = await application.post("sync/serverUuidStatus");
      if (response?.data?.message === "All servers have the same UUID") {
        setMismatchDialogOpen(true);
        setMessage("All servers are already in sync. No Sync needed");
      }
      setNotify({
        isOpen: true,
        status: response?.data?.message,
        type: "success",
        pagename: pageName,
        message: response?.data?.messageDetail,
      });
    } catch (error) {
      if (
        error.response.data.message === "Not all servers have the same UUID"
      ) {
        setMismatchDialogOpen(true);
        setMessage("Not all servers have the same UUID");
      }
      setNotify({
        isOpen: true,
        message: "",
        type: "error",
        pagename: pageName,
        status: error.response.data.message,
      });
    }
  };

  const handelFinalSync = () => {
    setLoading(true);
    setMessage("Syncing All server, please wait...");
    application
      .post("sync/sync-data")
      .then((response) => {
        setLoading(false);
        fetchBackupList();
        fetchStatusList();
        setMessage("");
        setNotify({
          isOpen: true,
          status: response?.data?.message,
          type: "success",
          pagename: pageName,
        });
      })
      .catch((error) => {
        setLoading(false);
        setMessage("");
        setNotify({
          isOpen: true,
          message: error.response.data.messageDetail,
          type: "error",
          pagename: pageName,
          status: error.response.data.message,
        });
      });
  };

  const handelRefreshBackupList = () => {
    fetchBackupList();
    setNotify({
      isOpen: true,
      status: `Backup List Refresh Successfully`,
      type: "success",
      pagename: pageName,
    });
  };

  const handelRefreshStatusList = () => {
    fetchStatusList();
    setNotify({
      isOpen: true,
      status: `Server Status List Refresh Successfully`,
      type: "success",
      pagename: pageName,
    });
  };

  const handelClose = () => {
    setAnchorEl(null);
    setSelectedServer("");
  };

  const handleBackupRestore = (backup, event) => {
    setSelectedBackup(backup);
    setAnchorEl(event.currentTarget);
  };

  const restoreServer = async (selectedBackup, selectedServer) => {
    setLoading(true);
    setMessage(`Restoring backup on Server ${selectedServer}, please wait...`);
    application
      .post(`sync/backupRestore/${selectedBackup}/${selectedServer}`)
      .then((response) => {
        setLoading(false);
        setMessage("");
        fetchBackupList();
        fetchStatusList();
        setNotify({
          isOpen: true,
          message: response?.data?.messageDetail,
          type: "success",
          pagename: pageName,
          status: response?.data?.message,
        });
      })
      .catch((error) => {
        setLoading(false);
        setMessage("");
        setNotify({
          isOpen: true,
          message: error?.response?.data?.messageDetail,
          type: "error",
          pagename: pageName,
          status: error?.response?.data?.message,
        });
      });
  };

  const handleSubmitRestore = () => {
    if (selectedBackup && selectedServer) {
      restoreServer(selectedBackup.id, selectedServer.nodeIp);
      setNotify({
        isOpen: true,
        status: `Restoring", ${selectedBackup.versionId}, "to", ${selectedServer.nodeIp}`,
        type: "info",
        pagename: pageName,
      });
      setAnchorEl(null);
      setSelectedServer("");
    }
  };

  function formatDateTime(isoDateStr) {
    const date = new Date(isoDateStr);
    const options = {
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return date.toLocaleString("en-US", options).replace(",", " at");
  }

  const truncateFileName = (fileName) => {
    if (!fileName) return "";
    return fileName.length > 15 ? `${fileName.slice(0, 15)}...` : fileName;
  };


  const handleVersionToggle = (index) => {
    setExpandedVersionIndex((prev) => (prev === index ? null : index));
    setExpandedIPIndex({});
  };

  const handleIPToggle = (vIndex, ipIndex) => {
    setExpandedIPIndex((prev) => ({
      ...prev,
      [vIndex]: prev[vIndex] === ipIndex ? null : ipIndex,
    }));
  };

  const handleBackupToggle = (index) => {
    setExpandedBackupIndex((prev) => (prev === index ? null : index));
    setExpandedBackupIPIndex({});
  };

  const handleBackupIPToggle = (vIndex, ipIndex) => {
    setExpandedBackupIPIndex((prev) => ({
      ...prev,
      [vIndex]: prev[vIndex] === ipIndex ? null : ipIndex,
    }));
  };

  const groupStatusByIP = (backups) => {
    const map = {};
    backups?.forEach((item) => {
      if (!map[item.servers]) {
        map[item.servers] = [];
      }
      map[item.servers].push(item);
    });
    return map;
  };

  const groupBackupByIP = (files) => {
    const map = {};
    files?.forEach((file) => {
      if (!map[file.ip]) {
        map[file.ip] = [];
      }
      map[file.ip].push(file);
    });
    return map;
  };

  return (
    <Box sx={{ p: 2 }}>
      {loading && (
        <Backdrop
          open={loading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress />
          <MDTypography variant="h6" sx={{ m: 2 }}>
            {message ? message : "In Progress..."}
          </MDTypography>
        </Backdrop>
      )}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <Typography
          variant="h4"
          sx={{ p: 1, display: "flex", alignItems: "center" }}
        >
          <StorageIcon sx={{ mr: 2 }} />
          Sync All Server
        </Typography>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: 3,
              height: "100%",
              minHeight: "400px",
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              position="relative"
            >
              <Typography variant="h6" sx={{ p: 2 }}>
                Server Status List
              </Typography>
              <Tooltip title="Refresh">
                <IconButton
                  color="info"
                  onClick={() => handelRefreshStatusList()}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ p: 2 }}>
              <List dense>
                {statusList?.map((versionItem, versionIndex) => {
                  const ipMap = groupStatusByIP(versionItem.lastBackUpModel2s);
                  return (
                    <Box key={versionIndex}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar>
                            <SyncIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <Box sx={{ flexGrow: 1, pl: 2 }}>
                          <MDTypography variant="h6">
                            {versionItem.version}
                          </MDTypography>
                          <MDTypography variant="subtitle2">
                            {formatDateTime(versionItem.time)}
                          </MDTypography>
                        </Box>
                        <IconButton
                          color="text"
                          onClick={() => handleVersionToggle(versionIndex)}
                        >
                          {expandedVersionIndex === versionIndex ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </ListItem>
                      <Collapse
                        in={expandedVersionIndex === versionIndex}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List dense sx={{ pl: 4 }}>
                          {Object.entries(ipMap).map(
                            ([ip, backups], ipIndex) => (
                              <Box key={ip}>
                                <ListItem>
                                  <IconButton color="text">
                                    <StorageIcon />
                                  </IconButton>
                                  <Box sx={{ flexGrow: 1, pl: 2 }}>
                                    <MDTypography variant="subtitle2">
                                      {ip}
                                    </MDTypography>
                                  </Box>
                                  <IconButton
                                    color="text"
                                    onClick={() =>
                                      handleIPToggle(versionIndex, ipIndex)
                                    }
                                  >
                                    {expandedIPIndex[versionIndex] ===
                                    ipIndex ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                </ListItem>
                                <Collapse
                                  in={expandedIPIndex[versionIndex] === ipIndex}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <List dense sx={{ pl: 6 }}>
                                    {backups.map((backup, bIndex) => (
                                      <ListItem key={bIndex}>
                                        <Tooltip title={backup.backUpFileName}>
                                          <MDTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.8rem",
                                              flexGrow: 1,
                                            }}
                                          >
                                            {bIndex + 1} -{" "}
                                            {truncateFileName(
                                              backup.backUpFileName
                                            )}
                                          </MDTypography>
                                        </Tooltip>
                                        <MDTypography
                                          variant="body2"
                                          sx={{ fontSize: "0.8rem" }}
                                        >
                                          {backup.status}
                                        </MDTypography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Collapse>
                              </Box>
                            )
                          )}
                        </List>
                      </Collapse>

                      <Divider />
                    </Box>
                  );
                })}

                {statusList.length === 0 && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Box
                      component="img"
                      src="/images/cloud-server.png"
                      sx={{ width: 80, height: 80, mb: 1 }}
                    />
                    <MDTypography variant="subtitle2">
                      No Status Available
                    </MDTypography>
                  </Box>
                )}
              </List>
            </Box>
          </Card>
        </Grid>
        {/* Sync  Server*/}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              position="relative"
            >
              <Typography variant="h6" sx={{ p: 2 }}>
                Sync Server
              </Typography>
            </Box>
            <Box position="relative" sx={{ mb: 6 }}>
              <Box sx={{ flexGrow: 1, mt: 4 , mb: 4, p: 2 }}>
                <Grid
                  container
                  spacing={2}
                  justifyContent={
                    serverList?.length === 1
                      ? "center"
                      : serverList?.length === 2
                      ? "space-evenly"
                      : "space-evenly"
                  }
                >
                  {serverList &&
                    serverList.map((item, index) => (
                      <Grid
                        item
                        key={index}
                        xs={6}
                        sm={4}
                        md={3}
                        textAlign="center"
                      >
                        <Box sx={{ zIndex: 2 }}>
                          <Box
                            component="img"
                            src={
                              item.serverType == "Source"
                                ? `/images/sync.png`
                                : `/images/servers.png`
                            }
                            alt={`Server`}
                            sx={{
                              width: 50,
                              height: 50,
                            }}
                          />
                          <MDTypography
                            variant="subtitle2"
                            sx={{ fontSize: "0.9rem" }}
                          >
                            {item.nodeIp}
                          </MDTypography>
                          <MDTypography
                            variant="subtitle2"
                            sx={{ fontSize: "1rem" }}
                          >
                            {item.serverType}
                          </MDTypography>
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              </Box>
            </Box>

            <Box display="flex" alignItems="center" justifyContent="center">
              <MDButton color="info" onClick={() => handleSyncCheck()}>
                Start Sync All Servers
              </MDButton>
            </Box>
          </Card>
        </Grid>
        {/* Last 5 Backup List */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, height: "100%" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              position="relative"
            >
              <Typography variant="h6" sx={{ p: 2 }}>
                Last 5 Backup List
              </Typography>
              <Tooltip title="Refresh">
                <IconButton
                  color="info"
                  onClick={() => handelRefreshBackupList()}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ p: 2 }}>
              <List dense>
                {backupList?.map((versionItem, versionIndex) => {
                  const ipMap = groupBackupByIP(versionItem?.files || []);

                  return (
                    <Box key={versionIndex}>
                      <ListItem>
                        <ListItemAvatar>
                          <Avatar>
                            <CloudIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <Box sx={{ flexGrow: 1, pl: 2 }}>
                          <MDTypography variant="h6">
                            {versionItem.versionId}
                          </MDTypography>
                          <MDTypography variant="subtitle2">
                            {formatDateTime(versionItem.backupTime)}
                          </MDTypography>
                        </Box>
                        <IconButton
                          color="text"
                          onClick={() => handleBackupToggle(versionIndex)}
                        >
                          {expandedBackupIndex === versionIndex ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                        <Tooltip title="Restore Backup">
                          <IconButton
                            size="large"
                            color="warning"
                            onClick={(event) =>
                              handleBackupRestore(versionItem, event)
                            }
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      </ListItem>

                      <Collapse
                        in={expandedBackupIndex === versionIndex}
                        timeout="auto"
                        unmountOnExit
                      >
                        <List dense sx={{ pl: 4 }}>
                          {Object.entries(ipMap).map(
                            ([ip, backups = []], ipIndex) => (
                              <Box key={ip}>
                                <ListItem>
                                  <IconButton color="text">
                                    <StorageIcon />
                                  </IconButton>
                                  <Box sx={{ flexGrow: 1, pl: 2 }}>
                                    <MDTypography variant="subtitle2">
                                      {ip}
                                    </MDTypography>
                                  </Box>
                                  <IconButton
                                    color="text"
                                    onClick={() =>
                                      handleBackupIPToggle(
                                        versionIndex,
                                        ipIndex
                                      )
                                    }
                                  >
                                    {expandedBackupIPIndex[versionIndex] ===
                                    ipIndex ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                </ListItem>

                                <Collapse
                                  in={
                                    expandedBackupIPIndex[versionIndex] ===
                                    ipIndex
                                  }
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <List dense sx={{ pl: 6 }}>
                                    {backups?.map((backup, bIndex) => (
                                      <ListItem key={bIndex}>
                                        <Tooltip title={backup.path}>
                                          <MDTypography
                                            variant="body2"
                                            sx={{
                                              fontSize: "0.8rem",
                                              flexGrow: 1,
                                            }}
                                          >
                                            {bIndex + 1} -{" "}
                                            {truncateFileName(backup.dbName)}
                                          </MDTypography>
                                        </Tooltip>
                                        <MDTypography
                                          variant="body2"
                                          sx={{ fontSize: "0.8rem" }}
                                        >
                                          {backup.size}
                                        </MDTypography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Collapse>
                              </Box>
                            )
                          )}
                        </List>
                      </Collapse>

                      <Divider />
                    </Box>
                  );
                })}

                {statusList.length === 0 && (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                  >
                    <Box
                      component="img"
                      src="/images/cloud-server.png"
                      sx={{ width: 80, height: 80, mb: 1 }}
                    />
                    <MDTypography variant="subtitle2">
                      No Backup Available
                    </MDTypography>
                  </Box>
                )}
              </List>
            </Box>
          </Card>
        </Grid>
      </Grid>
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
      >
        <Box sx={{ px: 2, py: 1, width: 250 }}>
          <MDTypography variant="subtitle2" gutterBottom>
            Select Server to Restore
          </MDTypography>
          <FormControl fullWidth>
            <Select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              displayEmpty
              size="small"
              style={{ height: "40px" }}
              sx={{
                marginBottom: "4px",
                "& option": {
                  color: "rgba(0, 0, 0, 0.87)",
                },
              }}
            >
              {serverList.length !== 0 ? (
                <MenuItem value=""> Select Server to Restore</MenuItem>
              ) : (
                <MenuItem value=""> No Server Available</MenuItem>
              )}
              {serverList
                .filter((server) => server?.serverType !== "Source")
                .map((server, idx) => (
                  <MenuItem key={idx} value={server}>
                    {server.nodeIp}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
            <MDButton
              size="small"
              variant="contained"
              color="secondary"
              onClick={() => handelClose()}
            >
              Cancel
            </MDButton>
            <MDButton
              size="small"
              variant="contained"
              color="info"
              onClick={() => handleSubmitRestore()}
            >
              Restore
            </MDButton>
          </Box>
        </Box>
      </Menu>
      <Dialog
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
        open={mismatchDialogOpen}
        onClose={() => setMismatchDialogOpen(false)}
      >
        <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          Mismatch detected!
        </DialogTitle>
        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <Typography variant="body1">{message}</Typography>
        </DialogContent>
        <DialogActions sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          <MDButton
            onClick={() => setMismatchDialogOpen(false)}
            color="secondary"
          >
            Cancel
          </MDButton>
          <MDButton
            onClick={() => {
              setMismatchDialogOpen(false);
              handelFinalSync();
            }}
            color="primary"
            variant="contained"
          >
            Force Sync
          </MDButton>
        </DialogActions>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </Box>
  );
};

export default ServerBackupRestore;
